'use client';
import React, { useRef } from "react";
import Headline from "@/components/ui/Headline/Headline";
import { LanguageType, LanguageDirection } from "@/lib/types";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import useAspectRatio from "@/hooks/useAspectRatio";
import Image from 'next/image';

import { debug } from '@/lib/debug';
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP, ScrollTrigger);

interface MissionSectionProps {
  language?: LanguageType
  headlineText: string
  subheadingText: string
  paragraphText: string
  imageSrc: string
  imageAlt: string
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};



// TODO: still to be rechecked and worked on after possible change?

const MissionSection: React.FC<MissionSectionProps> = ({
  language = "english",
  headlineText,
  subheadingText,
  paragraphText,
  imageSrc,
  imageAlt,
}) => {
  const aspectRatio = useAspectRatio();
  const isLg = useIsLg();
  const isTb = useIsTb();
  const sectionRef = useRef<HTMLElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  // const mobileAnimInitialized = useRef(false);

  // debug.log("rerender");

  useGSAP(() => {
    debug.log("aspectRatio", aspectRatio);
    // debug.log("mobileAnimInitialized.current", mobileAnimInitialized.current);

    // if (aspectRatio === 0) {
    //   return;
    // }

    const section = sectionRef.current;
    const imageContainer = imageContainerRef.current;
    const imageWrapper = imageWrapperRef.current;
    if (section && imageContainer && imageWrapper) {

      if (isLg) {
        // mobileAnimInitialized.current = false;

        gsap.set(imageContainer, {
          borderRadius: 0,
          width: "100%",
          maxWidth: "100vw",
          aspectRatio: aspectRatio,
          backgroundSize: "cover",
        });


        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: imageContainer,
            // start: "bottom bottom",
            // end: "center center-=40%",
            start: "top center+=20%",
            end: "top top+=20%",
            // end: "+=100%",
            scrub: true,
            // pin: section,
            // pinSpacing: true,
            // markers: true,
            id: "mission-section-pin",
            onLeave: () => {
              ScrollTrigger.refresh();
            },
            // onLeaveBack: () => {
            //   ScrollTrigger.refresh();
            // }
          },
        });

        timeline
          .to(imageContainer, {
            marginTop: "7rem",
            width: "100%",
            maxWidth: "113rem",
            aspectRatio: "3.33 / 1", // 10/3
            borderRadius: "32px",
            ease: "none",
          })
          .to(imageWrapper, {
            paddingLeft: "3.5rem",
            paddingRight: "3.5rem",
          }, "<");

      }
    }

    // Refresh so downstream ScrollTriggers (e.g. footer sun) recalculate
    // after this section's height changed due to aspectRatio/breakpoint change
    ScrollTrigger.refresh();

  }, { dependencies: [isLg, aspectRatio], revertOnUpdate: true });

  useGSAP(() => {
    if (!isLg) {
      const section = sectionRef.current;
      if (section) {
        const images: HTMLElement[] = Array.from(section.querySelectorAll('.mission-mobile-parallax-image'));

        images.forEach(image => {
          gsap.fromTo(image, {
            yPercent: -10
          }, {
            yPercent: 10,
            ease: "none",
            scrollTrigger: {
              trigger: image,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
              onEnter: () => {
                image.style.willChange = "transform";
              },
              onLeave: () => {
                image.style.willChange = "auto";
              },
              onEnterBack: () => {
                image.style.willChange = "transform";
              },
              onLeaveBack: () => {
                image.style.willChange = "auto";
              }
            }
          });
        });
      }
    }
  }, { dependencies: [isLg, aspectRatio], revertOnUpdate: true });

  useGSAP(() => {
    const paragraphEl = paragraphRef.current;
    if (!paragraphEl) return;

    // Set initial state
    gsap.set(paragraphEl, {
      autoAlpha: 0,
      filter: 'blur(30px)',
      y: +100,
      force3D: true,
    });

    // Create scroll trigger animation
    gsap.to(paragraphEl, {
      autoAlpha: 1,
      filter: 'blur(0px)',
      y: 0,
      duration: 1.0,
      ease: "power2.out",
      force3D: true,
      scrollTrigger: {
        trigger: paragraphEl,
        start: isLg ? "bottom bottom-=10%" : "bottom bottom",
        toggleActions: "play none none none"
      }
    });
  }, [paragraphText, isLg]);

  return (
    <section
      ref={sectionRef}
      // padding-main-x-desktop
      className="
        w-full
        flex flex-col items-center justify-center lg:justify-start
        pt-[2rem]
        padding-main-x-mobile
        lg:px-0
        tb:pt-0
        gap-[2.5rem] lg:gap-[4.5rem]
      ">
      <div className="
        w-full container-large
        padding-main-x-desktop
        ">
        <div
          style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
          className="
            w-full flex flex-row justify-between gap-x-[4rem] flex-wrap
            gap-y-[1.5rem] lg:gap-y-[2rem]
            px-[0.5rem] lg:px-0
        ">
          <Headline
            type="long"
            alignment={language === "english" ? 'left' : 'right'}
            heading={headlineText}
            subheading={subheadingText}
            language={language}
          />

          <div className="flex flex-col lg:items-end lg:justify-end tb:flex-grow tb:w-[30.6875rem] tb:max-w-[37rem]">
            <p
              ref={paragraphRef}
              className={`
            ${language === 'arabic' ?
                'text-right'
                :
                'text-left'
              }
              text-text-tertiary
              text-18--alexandria
            `}
            style={{ whiteSpace: 'pre-line' }}>
              {paragraphText}
            </p>
          </div>

        </div>
      </div>

      {isLg ?
        <div ref={imageWrapperRef} className="w-full flex items-center justify-center">
          <div
            ref={imageContainerRef}
            style={{
              backgroundImage: `url(${imageSrc})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              aspectRatio: isLg ? 10 / 3 : isTb ? 10 / 3 : 9 / 16,
              // aspectRatio: 10 / 3,
              width: "100%",
              maxWidth: isLg ? "100vw" : '100%',
              borderRadius: isLg ? '0' : '24px',
            }}
            // container-x-large
            // flex flex-col items-center justify-center
            // w-full
            // border-radius-main
            className="
          "
            role="img"
            aria-label={imageAlt}
          />
        </div>
        :
        <div
          style={{ aspectRatio: isTb ? '10 / 3' : '3 / 4' }} //'0.5625 / 1' }} // 9/16
          className="
              relative
              w-full
              overflow-hidden
              border-radius-main
              outline-secondary
              outline-stroke-soft-200
            ">
          <Image
            src={imageSrc}
            alt={imageAlt}
            fill
            sizes="100vw"
            loading="eager"
            className="mission-mobile-parallax-image"
            style={{ objectFit: 'cover', scale: 1.1 }}
          />
        </div>
      }
    </section>
  );
}


export default MissionSection;
